import { ChangeDetectionStrategy, Component, computed, inject, ViewEncapsulation } from '@angular/core';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDialog } from '@angular/material/dialog';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';
import { FullscreenLayoutAreas } from '@src/assets/css/fullscreen.template.scss';
import { AppService } from '@src/suite/app.service';
import { PSTFieldNames } from '@src/suite/common/data-models/fields/pst-field-names';
import { CardViewType } from '@src/suite/common/enums/enums';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import { ViewStateLayouts } from '@src/suite/common/enums/viewstate-layouts';
import { ViewStateTypes } from '@src/suite/common/enums/viewstate-types';
import { NomWebServicesJson } from '@src/suite/common/services/nom-web-services-json';
import { VisualStateService } from '@src/suite/common/services/visual-state-service';
import { OpenFullscreenModalActions } from '@src/suite/common/store/fullscreen/actions/fullscreen.actions';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { selectGroupByOptions, selectLayoutOptions, selectListNewGuids, selectListNewItems, selectSortByOptions } from '@src/suite/common/store/list/list.selectors';
import { ViewStateActions } from '@src/suite/common/store/navigation/actions/viewstate.actions';
import {
    selectCurrentGroupBy,
    selectCurrentGroupByOrderIcon,
    selectCurrentGroupByOrderTitle,
    selectCurrentLayoutValue,
    selectCurrentSortBy,
    selectCurrentSortByOrderIcon,
    selectCurrentSortByOrderTitle,
    selectCurrentViewState,
    selectGroupByDateTimeField,
} from '@src/suite/common/store/navigation/selectors/viewstate.selectors';
import { RecordHttpActions } from '@src/suite/common/store/records/records.actions';
import {
    isSingleViewStateRecord,
    isViewStateSupportsGroupBy,
    isViewStateSupportsGroupByOrder,
    isViewStateSupportsLayout,
    isViewStateSupportsSortBy,
    isViewStateSupportsSortByOrder,
} from '@src/suite/common/utils/viewstate-utils';

@Component({
    selector: 'st-base-view-options',
    imports: [MatButtonToggleModule, MatListModule, MatMenuModule, MatTooltipModule],
    template: '',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class BaseViewOptionsComponent {
    store = inject(Store);
    appService = inject(AppService);
    visualStateService = inject(VisualStateService);
    nomWebServicesJson = inject(NomWebServicesJson);
    dialog = inject(MatDialog);

    ObjectTypes = ObjectTypes;
    cardViewType = CardViewType;

    isDateField = this.store.selectSignal(selectGroupByDateTimeField);
    languageSettings = this.store.selectSignal(selectI18N)();
    selectCurrentGroupByOrderIcon = this.store.selectSignal(selectCurrentGroupByOrderIcon);
    selectCurrentSortByOrderIcon = this.store.selectSignal(selectCurrentSortByOrderIcon);

    viewStateGroupByOrderTitle = this.store.selectSignal(selectCurrentGroupByOrderTitle);
    viewStateSortByOrderTitle = this.store.selectSignal(selectCurrentSortByOrderTitle);

    viewStateLayoutOptions = this.store.selectSignal(selectLayoutOptions);
    viewStateGroupByOptions = this.store.selectSignal(selectGroupByOptions);
    viewStateSortByOptions = this.store.selectSignal(selectSortByOptions);

    viewStateLayoutValue = this.store.selectSignal(selectCurrentLayoutValue);
    viewStateGroupByValue = this.store.selectSignal(selectCurrentGroupBy);
    viewStateSortByValue = this.store.selectSignal(selectCurrentSortBy);

    readonly viewStateLoaded = computed(() => {
        return !!this.viewState();
    });

    readonly viewState = computed(() => this.store.selectSignal(selectCurrentViewState)());

    readonly newlyAddedItems = computed(() => {
        return this.store.selectSignal(selectListNewItems)();
    });

    // The poller is the authority on how many new items exist: it reports guids, and the records behind them
    // are only fetched when the menu opens (loadNewItems). Counting resolved records instead hid the button
    // entirely whenever the store had not been handed those records — bug 110552.
    private readonly newItemGuids = this.store.selectSignal(selectListNewGuids);

    readonly newItemsCount = computed(() => this.newItemGuids().length);

    // Menu is open with a count but the records have not landed yet.
    readonly newItemsLoading = computed(() => this.newItemsCount() > 0 && !this.newlyAddedItems().length);

    readonly newItemsCountLabel = computed(() => {
        return `${this.newItemsCount()} ${this.newItemsCount() === 1 ? this.languageSettings.PSTWebNewItemLabel : this.languageSettings.PSTWebNewItemsLabel}`;
    });

    readonly shouldShowLayoutOptions = computed(() => {
        return !this.visualStateService.isLessThanSmall();
    });

    readonly viewStateSupportsLayout = computed(() => {
        return isViewStateSupportsLayout(this.viewState());
    });

    readonly viewStateSupportsGroupBy = computed(() => {
        return isViewStateSupportsGroupBy(this.viewState());
    });

    readonly viewStateSupportsGroupByOrder = computed(() => {
        return isViewStateSupportsGroupByOrder(this.viewState());
    });

    readonly viewStateSupportsSortBy = computed(() => {
        return isViewStateSupportsSortBy(this.viewState());
    });

    readonly viewStateSupportsSortByOrder = computed(() => {
        return isViewStateSupportsSortByOrder(this.viewState());
    });

    readonly viewStateGroupByOrderDisabled = computed(() => {
        if (
            isSingleViewStateRecord(this.viewState()) &&
            ((this.viewState() as StorytellingSingleViewStateRecord).PSTViewStateLayout?.Value === ViewStateLayouts.MAP ||
                (this.viewState() as StorytellingSingleViewStateRecord).PSTViewStateLayout?.Value === ViewStateLayouts.TILE ||
                (this.viewState() as StorytellingSingleViewStateRecord).PSTViewStateSorting?.Value === PSTFieldNames.PSTTopicOrder)
        ) {
            return true;
        }
        return false;
    });

    readonly groupByDisabled = computed(() => {
        if (
            isSingleViewStateRecord(this.viewState()) &&
            ((this.viewState() as StorytellingSingleViewStateRecord).PSTViewStateLayout?.Value === ViewStateLayouts.MAP ||
                (this.viewState() as StorytellingSingleViewStateRecord).PSTViewStateType === ViewStateTypes.SEARCH ||
                (this.viewState() as StorytellingSingleViewStateRecord).PSTViewStateSorting?.Value === PSTFieldNames.PSTTopicOrder)
        ) {
            return true;
        }
        return false;
    });

    readonly sortByDisabled = computed(() => {
        if (
            isSingleViewStateRecord(this.viewState()) &&
            ((this.viewState() as StorytellingSingleViewStateRecord).PSTViewStateLayout?.Value === ViewStateLayouts.MAP ||
                (this.viewState() as StorytellingSingleViewStateRecord).PSTViewStateSorting?.Value === PSTFieldNames.PSTTopicOrder)
        ) {
            return true;
        }
        return false;
    });

    readonly viewStateSortDisabled = computed(() => {
        if (
            isSingleViewStateRecord(this.viewState()) &&
            ((this.viewState() as StorytellingSingleViewStateRecord).PSTViewStateLayout?.Value === ViewStateLayouts.MAP ||
                (this.viewState() as StorytellingSingleViewStateRecord).PSTViewStateSorting?.Value === PSTFieldNames.PSTTopicOrder)
        ) {
            return true;
        }
        return false;
    });

    readonly viewStateSortingManual = computed(() => {
        if (isSingleViewStateRecord(this.viewState()) && (this.viewState() as StorytellingSingleViewStateRecord).PSTViewStateSorting?.Value === PSTFieldNames.PSTTopicOrder) {
            return true;
        }
        return false;
    });

    public clickNewItemsBtn() {
        this.store.dispatch(RecordHttpActions.loadNewItems());
    }

    openTopic(data: StorytellingTopicRecord) {
        this.store.dispatch(
            OpenFullscreenModalActions.openTopicFullscreenDialog({
                recordGuid: data?.Guid,
                recordType: data?.Type,
                currentNodeUUID: 'topic-' + data.Guid,
            })
        );
    }

    onAssignmentOpenHandler(record: StorytellingAssignmentRecord) {
        this.store.dispatch(
            OpenFullscreenModalActions.openAssignmentFullscreenDialog({
                recordGuid: record.Guid,
                recordType: record.Type,
                isMetadata: false,
                sidecarMenu: FullscreenLayoutAreas.SIDECAR_MAIN,
                currentNodeUUID: record.topicMetadata.Guid,
            })
        );
    }

    openStory(data: StorytellingStoryRecord, sourceRecord?: StorytellingTopicRecordWithUniqueId, standaloneStory?: boolean) {
        //TODO: Ask if the sourceRecord is needed!!!!!
        this.store.dispatch(
            OpenFullscreenModalActions.openStoryFullscreenDialog({
                recordGuid: data?.Guid,
                recordType: data?.Type,
                listGuid: sourceRecord?.Guid || data?.ParentGuid || data?.Guid,
                standaloneStory: standaloneStory || false,
                currentNodeUUID: sourceRecord?.uniqueId || 'story-' + data.Guid,
            })
        );
    }

    openRundown(data: StorytellingRundownRecord) {
        this.store.dispatch(
            OpenFullscreenModalActions.openRundownFullscreenDialog({
                recordGuid: data.Guid,
                recordType: data.Type,
            })
        );
    }

    updateLayout(layout: string) {
        this.store.dispatch(ViewStateActions.updateViewStateLayout({ layout }));
    }
    updateGroupBy(groupBy: string) {
        this.store.dispatch(ViewStateActions.updateViewStateGroupBy({ groupBy }));
    }
    updateSortBy(sortBy: string) {
        this.store.dispatch(ViewStateActions.updateViewStateSortBy({ sortBy }));
    }
    updateSortOrder() {
        this.store.dispatch(ViewStateActions.updateViewStateSortBySortOrder());
    }
    updateGroupSortOrder() {
        this.store.dispatch(ViewStateActions.updateViewStateGroupBySortOrder());
    }
}
